import { SignInLinkStyles } from "@husky-hrc/Components/SignIn/SignInLink";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import groupBy from "lodash/groupBy";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    heading = "heading",
    title = "title",
    links = "links",
}

interface OwnLinkModel {
    fields: {
        openInNewWindow: boolean;
        overriddenTitle: string;
        destination: LinkFieldValue;
        linkColor: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.heading]: string;
        [fields.title]: string;
        [fields.links]: OwnLinkModel[];
    };
    extendedStyles?: DocumentationListStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const fieldLinks = ownProps.fields.links;
    const links = mapLinks<
        OwnLinkModel,
        {
            openInNewWindow: boolean;
            overriddenTitle: string;
            linkColor: string;
        }
    >(state, fieldLinks, widgetLink => ({
        openInNewWindow: widgetLink.fields.openInNewWindow,
        overriddenTitle: widgetLink.fields.overriddenTitle,
        linkColor: widgetLink.fields.linkColor,
    }));

    const groupedLinks = groupBy(links, link => link);
    return {
        groupedLinks,
        isAuthenticated: state.context.session.isAuthenticated,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const TitleWrapper = getStyledWrapper("div");

export interface DocumentationListStyles {
    mainContainerWrapper: InjectableCss;
    documentationLinksWrapper: InjectableCss;
    titleWrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    linkWrapper?: InjectableCss;
    linkTextStyles?: LinkPresentationProps;
    signInLinkStyles?: SignInLinkStyles;
}

export const documentationListStyles: DocumentationListStyles = {
    mainContainerWrapper: {
        css: css`
            background-color: ${getColor("common.accent")};
            margin: 2rem 0;
            padding: 2rem;
        `,
    },
    documentationLinksWrapper: {
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            display: grid;
                            grid-template-columns: 1fr;
                            grid-gap: 1rem 2rem;
                        `,
                        null,
                        null,
                        css`
                            grid-template-columns: 1fr 1fr;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
            color: ${getColor("danger.main")};
            background-color: ${getColor("common.background")};
            padding: 1rem;
        `,
    },
    title: {
        variant: "h2",
        css: css`
            color: ${getColor("text.main")};
            margin-bottom: 2rem;
            font-family: IndustryInc, Lato, "Open Sans", Roboto, sans-serif;
            font-size: 2rem;
            letter-spacing: 1px;
        `,
    },
    linkWrapper: {
        css: css`
            font-weight: bold;
            color: ${getColor("danger.main")};
            background-color: ${getColor("common.background")};
            padding: 1rem;
            position: relative;

            > a {
                display: flex;
                justify-content: space-between;
                width: 100%;
                height: 100%;

                ::after {
                    content: url("/UserFiles/documentation-pdf-icon.svg");
                    position: absolute;
                    right: 1rem;
                }
            }
        `,
    },
    linkTextStyles: {
        typographyProps: {
            variant: "headerTertiary",
            transform: "none",
        },
        css: css`
            display: inline-block;
            max-width: 85%;
        `,
    },
};

const DocumentationList: React.FC<Props> = ({ fields, groupedLinks, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(documentationListStyles, extendedStyles));

    const heading = fields.heading;

    return (
        <StyledWrapper {...styles.mainContainerWrapper}>
            <TitleWrapper>
                <Typography tabIndex={-1} data-id="PageTitle" {...styles.title} weight={400}>
                    {heading}
                </Typography>
            </TitleWrapper>
            <StyledWrapper {...styles.documentationLinksWrapper}>
                {Object.keys(groupedLinks).map((linkGroup, linkGroupIndex) => (
                    <React.Fragment key={linkGroupIndex}>
                        {groupedLinks[linkGroup].map(
                            (link, linkIndex) =>
                                link.url && (
                                    <StyledWrapper key={`${linkIndex}`} {...styles.linkWrapper}>
                                        <Link
                                            {...styles.linkTextStyles}
                                            color={link.linkColor}
                                            href={link.url}
                                            target={link.openInNewWindow ? "_blank" : "_self"}
                                        >
                                            {link.overriddenTitle || link.title}
                                        </Link>
                                    </StyledWrapper>
                                ),
                        )}
                    </React.Fragment>
                ))}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(DocumentationList),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.heading,
                editorTemplate: "TextField",
                displayName: "Heading",
                defaultValue: "",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        let displayValue = "";
                        const categories = (state as any).pageEditor.categories;
                        if (!categories) {
                            displayValue = "Loading...";
                        } else {
                            const matchingCategories = categories.filter((o: { id: string }) => o.id === value);
                            if (matchingCategories.length === 0) {
                                displayValue = "Unknown";
                            } else {
                                displayValue = matchingCategories[0].shortDescription;
                            }
                        }
                        return displayValue;
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "linkColor",
                        editorTemplate: "ColorPickerField",
                        defaultValue: "black",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
